/**
 * Constroi a arvore mesclada de OUs AWS + tenant rows.
 *
 * Ver spec docs/superpowers/specs/2026-05-13-tenants-ou-tree-view-design.md
 * secao "Fluxo de dados" para o algoritmo completo.
 *
 * Modelo tenant-first (v1.46.2+): em vez de comecar dos "roots" (parent=null)
 * e promover org_roots, comecamos diretamente dos nos kind=tenant/sub_tenant
 * que nao tem um ancestral tenant. Isso lida naturalmente com org_roots
 * aninhados (ex: Amazonas Inovare dentro de Root) e com tenants em qualquer
 * profundidade.
 */
import type { AwsOuNode } from '@/api/awsOrgProvisioning'
import type { TenantRow } from '../TenantsTable'
import type {
  BuildOptions,
  MergedTreeNode,
  OuTreeBranch,
  OuTreeAccountLeaf,
} from './types'
import { countAccounts } from './types'
import { orderForEnv } from './envOrdering'

export function buildTenantOuTree(
  tenants: TenantRow[],
  ouNodes: AwsOuNode[],
  opts: BuildOptions,
): MergedTreeNode[] {
  // Lapide do soft-delete: `aws_ou_tree_service` marca `is_legacy` na OU que
  // sumiu da AWS e nunca apaga a linha, porque o historico importa. Esta tela
  // responde "o que existe agora", entao a lapide nao entra — desenhada, ela
  // vira um ambiente vazio indistinguivel de um vivo. Ver TASK-10146.
  //
  // `is_legacy` ausente conta como VIVA, mesma decisao de `envsVivos.ts`:
  // resposta velha em cache do navegador nao pode fazer OU sumir da tela.
  //
  // Tenants.tsx ja pede `include_legacy=false`; este filtro e a segunda
  // barreira, para cache velho e para qualquer chamador futuro.
  const liveOuNodes = ouNodes.filter((n) => !n.is_legacy)

  const ouById = new Map<string, AwsOuNode>()
  for (const n of liveOuNodes) ouById.set(String(n.ou_id), n)

  const byParent = new Map<string | null, AwsOuNode[]>()
  for (const n of liveOuNodes) {
    const k = n.parent_ou_id ?? null
    const arr = byParent.get(k) ?? []
    arr.push(n)
    byParent.set(k, arr)
  }

  const tenantsByOuId = new Map<string, TenantRow[]>()
  const noOuTenants: TenantRow[] = []
  for (const t of tenants) {
    if (!t.ou_id || t.ou_id.startsWith('auto:') || !ouById.has(t.ou_id)) {
      noOuTenants.push(t)
      continue
    }
    const arr = tenantsByOuId.get(t.ou_id) ?? []
    arr.push(t)
    tenantsByOuId.set(t.ou_id, arr)
  }

  const q = opts.searchQuery?.trim().toLowerCase() ?? ''
  const hasQuery = q.length > 0

  function tenantMatchesSearch(t: TenantRow): boolean {
    if (!hasQuery) return false
    return (
      t.name.toLowerCase().includes(q) ||
      (t.ou_id ?? '').toLowerCase().includes(q) ||
      (t.environment ?? '').toLowerCase().includes(q)
    )
  }

  function makeBranch(ou: AwsOuNode): OuTreeBranch {
    const childOus = sortOus(byParent.get(String(ou.ou_id)) ?? [])
    const childBranches: MergedTreeNode[] = childOus.map(makeBranch)
    const childAccounts: OuTreeAccountLeaf[] = (tenantsByOuId.get(String(ou.ou_id)) ?? [])
      .slice()
      .sort((a, b) =>
        a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }),
      )
      .map((t) => ({ type: 'account', tenant: t }))

    const ownMatch =
      hasQuery &&
      (ou.name.toLowerCase().includes(q) ||
        String(ou.ou_id).toLowerCase().includes(q))
    const descendantBranchMatch = childBranches.some(
      (c) => c.type === 'ou' && c.matchesSearch,
    )
    const descendantAccountMatch = childAccounts.some((a) =>
      tenantMatchesSearch(a.tenant),
    )

    return {
      type: 'ou',
      node: ou,
      children: [...childBranches, ...childAccounts],
      matchesSearch: ownMatch || descendantBranchMatch || descendantAccountMatch,
    }
  }

  // Top-level tenant UOs: kind=tenant ou kind=sub_tenant SEM ancestral
  // tenant/sub_tenant. Isso pula org_roots em qualquer profundidade
  // (Root, Amazonas Inovare aninhado, etc) e exclui naturalmente
  // sub_tenants dentro de categoria SaaS (cujo ancestral e o tenant pai).
  function hasTenantAncestor(ou: AwsOuNode): boolean {
    let current = ou
    while (current.parent_ou_id) {
      const parent = ouById.get(String(current.parent_ou_id))
      if (!parent) return false
      if (parent.kind === 'tenant' || parent.kind === 'sub_tenant') return true
      current = parent
    }
    return false
  }

  const topLevelTenantOus = sortOus(
    liveOuNodes.filter(
      (n) =>
        (n.kind === 'tenant' || n.kind === 'sub_tenant') &&
        !hasTenantAncestor(n),
    ),
  )

  // Marca quais accounts ficam dentro de uma sub-arvore de top-level tenant.
  // Accounts cujo ou_id e valido mas nao caem em nenhuma dessas sub-arvores
  // (ex: conta debaixo de PRD loose, sem tenant pai) viram orfas.
  const accountedFor = new Set<number>()
  function markAccountedAccounts(ou: AwsOuNode) {
    for (const t of tenantsByOuId.get(String(ou.ou_id)) ?? []) {
      accountedFor.add(t.id)
    }
    for (const child of byParent.get(String(ou.ou_id)) ?? []) {
      markAccountedAccounts(child)
    }
  }
  for (const root of topLevelTenantOus) markAccountedAccounts(root)

  const looseAccounts = tenants.filter(
    (t) =>
      !!t.ou_id &&
      !t.ou_id.startsWith('auto:') &&
      ouById.has(t.ou_id) &&
      !accountedFor.has(t.id),
  )
  const allOrphans = [...noOuTenants, ...looseAccounts]

  const roots: MergedTreeNode[] = topLevelTenantOus.map(makeBranch)

  let finalRoots = roots
  if (opts.pruneEmptyParents) {
    finalRoots = roots
      .map(pruneOuBranch)
      .filter((n): n is MergedTreeNode => n !== null)
  }

  if (allOrphans.length > 0) {
    const orphanRoot: MergedTreeNode = {
      type: 'orphan_root',
      children: allOrphans
        .slice()
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }),
        )
        .map((t): OuTreeAccountLeaf => ({ type: 'account', tenant: t })),
    }
    return [orphanRoot, ...finalRoots]
  }

  return finalRoots
}

/**
 * Remove recursivamente nos `ou` cuja sub-arvore inteira nao contenha
 * nenhum account. Retorna null se o no inteiro deve ser podado.
 *
 * Excecao: quando ha searchQuery, preserva o no caso ele ou algum
 * descendente case com a busca (matchesSearch=true) — assim usuario
 * encontra OUs vazias que casam pelo nome.
 */
function pruneOuBranch(node: MergedTreeNode): MergedTreeNode | null {
  if (node.type !== 'ou') return node
  const survivingChildren = node.children
    .map(pruneOuBranch)
    .filter((c): c is MergedTreeNode => c !== null)
  const next = { ...node, children: survivingChildren }
  if (survivingChildren.every((c) => c.type !== 'account') && countAccounts(next) === 0) {
    if (!node.matchesSearch) return null
  }
  return next
}

/**
 * Ordena OUs: kind=environment usa orderForEnv (PRD > STG > HML > QA > DEV),
 * resto vai alfabetico pt-BR (sensitivity base).
 */
function sortOus(ous: AwsOuNode[]): AwsOuNode[] {
  return ous.slice().sort((a, b) => {
    const aEnv = a.kind === 'environment'
    const bEnv = b.kind === 'environment'
    if (aEnv && bEnv) return orderForEnv(a.name) - orderForEnv(b.name)
    if (aEnv && !bEnv) return -1
    if (!aEnv && bEnv) return 1
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}
